import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  DotsVertical,
  EmptyState,
  Flag01,
  Menu,
  Modal,
  PageHeader,
  Pagination,
  Plus,
  SearchLg,
  Table,
  TextInput,
} from '@opendate-app/setlist';

import CountBadge from '../../shared/components/CountBadge';
import PromoCodeCSVImportButton from '../../CSVImporter/PromoCodeCSVImportButton';
import PromoCodeTypeBadges, { promoCodeAppliesDiscount } from '../../ConfirmTicketing/components/PromoCodeTypeBadges';

export const PROMO_CODES_PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const cardStyle = {
  marginTop: 24,
  borderRadius: '12px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  border: '1px solid var(--sl-border-secondary)',
  background: 'var(--sl-bg-primary)',
};

const discountText = (promoCode) => {
  const applies = promoCodeAppliesDiscount({
    revealsHiddenTickets: promoCode.reveal_hidden_tickets,
    percentage: promoCode.percentage,
    amount: promoCode.amount,
  });
  if (!applies) return '—';
  if (typeof promoCode.percentage === 'number') return `${promoCode.percentage}%`;
  return formatter.format(promoCode.amount);
};

const statusSupportingText = (promoCode) => {
  if (promoCode.active) return `Ends ${promoCode.human_end_time}`;
  if (promoCode.past) return `Ended ${promoCode.human_end_time}`;
  return `Starts ${promoCode.human_start_time}`;
};

const PromoCodeList = ({
  promoCodes,
  totalEntries,
  currentPage,
  loading,
  initialSearch,
  hasSearch,
  csrfToken,
  team,
  onSearch,
  onPageChange,
  onAdd,
  onEdit,
  onCopy,
  onDelete,
  onImportClose,
}) => {
  const [searchText, setSearchText] = useState(initialSearch || '');
  const [promoCodeToDelete, setPromoCodeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const mounted = useRef(false);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return undefined;
    }
    const timer = setTimeout(() => onSearchRef.current(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const totalPages = Math.max(Math.ceil(totalEntries / PROMO_CODES_PER_PAGE), 1);

  const closeDeleteModal = () => {
    if (!deleting) setPromoCodeToDelete(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(promoCodeToDelete);
    } finally {
      setDeleting(false);
      setPromoCodeToDelete(null);
    }
  };

  const columns = [
    { key: 'name', header: 'Name', primary: true, render: (promoCode) => promoCode.name },
    {
      key: 'type',
      header: 'Code type',
      render: (promoCode) => (
        <PromoCodeTypeBadges
          reveals={promoCode.reveal_hidden_tickets}
          discounts={promoCodeAppliesDiscount({
            revealsHiddenTickets: promoCode.reveal_hidden_tickets,
            percentage: promoCode.percentage,
            amount: promoCode.amount,
          })}
        />
      ),
    },
    { key: 'discount', header: 'Discount', render: discountText },
    {
      key: 'uses',
      header: 'Uses',
      render: (promoCode) => `${promoCode.number_claimed}/${promoCode.unlimited ? 'Unlimited' : promoCode.limit}${promoCode.uses_per_fan ? ` · ${promoCode.uses_per_fan} per fan` : ''}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (promoCode) => (
        <div>
          <Badge dot color={promoCode.active ? 'success' : 'error'} size="sm">
            {promoCode.active ? 'Active' : 'Inactive'}
          </Badge>
          <div className="text-muted small">{statusSupportingText(promoCode)}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '48px',
      render: (promoCode) => (
        <Menu
          placement="bottom-end"
          trigger={(
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`More actions for ${promoCode.name}`}
            />
          )}
          items={[
            { label: 'Edit', onClick: () => onEdit(promoCode) },
            { label: 'Copy', onClick: () => onCopy(promoCode) },
            { divider: true },
            { label: 'Delete', destructive: true, onClick: () => setPromoCodeToDelete(promoCode) },
          ]}
        />
      ),
    },
  ];

  const noCodesAtAll = !loading && promoCodes.length === 0 && !hasSearch;
  const noSearchResults = promoCodes.length === 0 && hasSearch;

  const empty = noSearchResults
    ? <EmptyState size="sm" icon={SearchLg} title="No promo codes found" description="Try a different search." />
    : null;

  return (
    <div>
      <PageHeader
        title={(
          <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
            Promo Codes
            <CountBadge count={totalEntries} />
          </span>
        )}
        actions={(
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <PromoCodeCSVImportButton csrfToken={csrfToken} team={team} onClose={onImportClose} />
            <Button variant="primary" size="sm" icon={Plus} onClick={onAdd}>Add a Code</Button>
          </div>
        )}
      />
      <div style={cardStyle}>
        {noCodesAtAll ? (
          <div className="text-center" style={{ padding: '48px 24px' }}>
            <EmptyState
              icon={Flag01}
              title="Universal Promo Codes"
              description="Set up promo codes that will work across all of your events."
              primaryAction={{ label: 'Add Promo Code', onClick: onAdd }}
            />
            <div className="mt-2">
              <PromoCodeCSVImportButton csrfToken={csrfToken} team={team} onClose={onImportClose} />
            </div>
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: '12px 24px', gap: 12 }}>
              <div style={{ maxWidth: 320, flexGrow: 1 }}>
                <TextInput
                  size="sm"
                  type="search"
                  aria-label="Search promo codes"
                  placeholder="Search"
                  leadingIcon={SearchLg}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <span className="text-muted small" data-testid="promo-code-result-count">
                {totalEntries.toLocaleString()} promo code{totalEntries === 1 ? '' : 's'}
              </span>
            </div>
            <Table
              columns={columns}
              data={promoCodes}
              getRowKey={(promoCode) => promoCode.id}
              loading={loading}
              loadingLabel="Loading promo codes..."
              empty={empty}
              stackBelow={640}
            />
            {totalPages > 1 && (
              <div style={{ padding: '12px 24px' }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              </div>
            )}
            <p className="text-muted small mb-0" style={{ padding: '12px 24px 16px' }}>
              To create a promo code for a specific event, head to the ticketing module for that event.
            </p>
          </>
        )}
      </div>
      <Modal
        open={!!promoCodeToDelete}
        onClose={closeDeleteModal}
        type="error"
        title="Delete promo code"
        description="Are you sure? This cannot be undone."
        footer={(
          <>
            <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete promo code'}
            </Button>
          </>
        )}
      />
    </div>
  );
};

PromoCodeList.propTypes = {
  promoCodes: PropTypes.array.isRequired,
  totalEntries: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  initialSearch: PropTypes.string,
  hasSearch: PropTypes.bool,
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  onSearch: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onImportClose: PropTypes.func,
};

export default PromoCodeList;
